import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as ProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import { ReactComponent as WarehouseIcon } from 'assets/icons/warehouse.svg';
import clsx from 'clsx';
import { TITLE_DATE_PATTERN } from 'constants/general';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import type ICardSession from './interfaces/ICardSession';

import styles from './sass/CardSession.module.scss';

function CardSession({
  title,
  entities,
  isActive = false,
  isDefaultExpanded = false,
  isDisabled = false,
  showViewResults = false,
  onEdit,
  onDelete,
  onViewResults,
}: ICardSession): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefaultExpanded);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const titleDateMatch = title.match(TITLE_DATE_PATTERN);

  const handleToggleExpanded = () => {
    setIsExpanded((prevIsExpanded) => !prevIsExpanded);
  };

  const handleEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteModalOpen(true);
  };

  const handleRejectDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = () => {
    setIsDeleteModalOpen(false);
    onDelete();
  };

  const handleViewResults = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onViewResults?.();
  };

  const getEntityIcon = (entityType?: string) => {
    if (entityType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER) {
      return <WarehouseIcon aria-hidden="true" />;
    }

    return <ProductIcon aria-hidden="true" />;
  };

  return (
    <>
      <article className={clsx(styles.container, { [styles.active]: isActive })}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {titleDateMatch ? (
              <>
                {titleDateMatch[1]}
                <span className={styles.titleDate}>{titleDateMatch[2]}</span>
              </>
            ) : title}
          </h3>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={handleEdit}
              disabled={isDisabled}
              aria-label={t('valueChainMap.aiSessionsEditSession')}
            >
              <PencilIcon aria-hidden="true" />
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={handleDelete}
              disabled={isDisabled}
              aria-label={t('valueChainMap.aiSessionsDeleteSession')}
            >
              <TrashIcon aria-hidden="true" />
            </button>
          </div>
        </div>

        <button
          type="button"
          className={styles.entitiesToggle}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
        >
          <span className={styles.entitiesLabel}>
            {t('valueChainMap.aiSessionsSelectedEntities')}
            {' '}
            <span className={styles.entitiesCount}>
              (
              {entities.length}
              )
            </span>
          </span>
          <ChevronIcon
            className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
            aria-hidden="true"
          />
        </button>

        {isExpanded ? (
          <div className={styles.entitiesList}>
            {entities.length ? entities.map((entity) => (
              <span key={entity.id} className={styles.entityChip}>
                {getEntityIcon(entity.type)}
                <span className={styles.entityName}>{entity.name}</span>
              </span>
            )) : (
              <span className={styles.emptyEntities}>
                {t('valueChainMap.aiSessionsNoSelectedEntities')}
              </span>
            )}
          </div>
        ) : null}

        {showViewResults ? (
          <div className={styles.viewResultsFooter}>
            <button
              type="button"
              className={styles.viewResultsButton}
              onClick={handleViewResults}
              disabled={isDisabled}
              data-i18n-key="valueChainMap.monitoring.results.viewResults"
            >
              {t('valueChainMap.monitoring.results.viewResults')}
            </button>
          </div>
        ) : null}
      </article>

      <DeleteModal
        open={isDeleteModalOpen}
        onReject={handleRejectDelete}
        isLoading={false}
        onConfirm={handleConfirmDelete}
        titleKey="valueChainMap.aiSessionsDeleteModalTitle"
        hintKey="valueChainMap.aiSessionsDeleteModalDescription"
      />
    </>
  );
}

export default CardSession;
